import React, { useEffect, useState, useMemo } from "react";
import { X, Loader2, Search, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api/client";

// ── Reserved-slot claim dialog (internal portal only) ────────────────────────
//
// Two-step: patient search → confirm-and-claim. On submit we call
// POST /api/scheduling/reserved-slots/claim which internally invokes /book.
//
// Response handling (contract from backend/CHANGELOG):
//   200 → dialog closes, onClaimed(response) — page reloads calendar
//   409 → inline message ("slot already booked" / "slot already blocked" /
//         "slot is in the past") + auto-refresh calendar on close
//   502 → "Booking failed at Raintree — the reserved slot has been reopened."
//         (backend guarantees the state row was rolled back — no phantom.)
//
// String-only time formatting. NO `new Date()` on slot_time / date.

function fmt12(hms) {
  if (!hms) return "";
  const [h, m] = hms.split(":");
  const hr = parseInt(h, 10);
  return `${((hr + 11) % 12) + 1}:${m} ${hr >= 12 ? "PM" : "AM"}`;
}

// Human-readable "Tue, Aug 26" from an ISO date string — no timezone
// arithmetic (split + Date constructed in local-noon to sidestep DST edges;
// this is display of a DATE, not a datetime, and pins allow local
// formatting of pure-date strings).
function fmtDateISO(iso) {
  if (!iso) return "";
  const [y, mo, da] = iso.split("-").map((s) => parseInt(s, 10));
  if (!y || !mo || !da) return iso;
  const d = new Date(y, mo - 1, da, 12, 0, 0);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export function ReservedClaimDialog({ open, slot, locationLabel, onClose, onClaimed, onConflict }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(""); // structured: {kind, msg}
  const [reopened, setReopened] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setResults([]);
    setSelected(null);
    setErr("");
    setReopened(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    const h = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get("/crm/patients", { params: { search: q, per_page: 20, page: 1 } });
        setResults(res.data?.patients || []);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(h);
  }, [query, open]);

  const claim = async () => {
    if (!slot || !selected) return;
    setSubmitting(true);
    setErr("");
    setReopened(false);
    try {
      const res = await api.post("/scheduling/reserved-slots/claim", {
        location_code: slot.location_code,
        date: slot.date,
        slot_time: slot.slot_time,
        patient_id: String(selected.id),
      });
      toast.success("Reserved slot claimed · direct booking created");
      onClaimed?.(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Failed to claim reserved slot";
      if (status === 409) {
        setErr({ kind: "conflict", msg: String(detail) });
        // Signal the parent to refresh calendar truth — the placeholder is
        // stale (already booked/blocked or in the past).
        onConflict?.();
      } else if (status === 502) {
        setErr({ kind: "book_failed", msg: String(detail) });
        setReopened(true);
        onConflict?.(); // reload so the slot re-renders as open
      } else {
        setErr({ kind: "other", msg: String(detail) });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !slot) return null;

  const patientName = selected ? [selected.first_name, selected.last_name].filter(Boolean).join(" ") || `Patient #${selected.id}` : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="reserved-claim-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Claim WestStar Reserved Slot</h3>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="reserved-claim-slot-summary">
              {locationLabel || slot.location_code} · {fmtDateISO(slot.date)} · {fmt12(slot.slot_time)}
            </p>
          </div>
          <button type="button" data-testid="reserved-claim-close" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!selected && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Search patient</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="reserved-claim-search-input"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Name, phone, or DOB"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searching && (
                  <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
                )}
              </div>
              <div className="mt-2 max-h-60 overflow-auto border border-slate-100 rounded-lg divide-y divide-slate-100" data-testid="reserved-claim-search-results">
                {results.length === 0 && query.trim().length >= 2 && !searching && (
                  <div className="px-3 py-3 text-sm text-slate-500" data-testid="reserved-claim-search-empty">No matches</div>
                )}
                {results.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`reserved-claim-result-${p.id}`}
                    onClick={() => setSelected(p)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between gap-2 text-sm"
                  >
                    <span>
                      <span className="font-medium text-slate-900">{[p.first_name, p.last_name].filter(Boolean).join(" ") || `Patient #${p.id}`}</span>
                      {p.date_of_birth && (
                        <span className="ml-2 text-xs text-slate-500">DOB {p.date_of_birth}</span>
                      )}
                    </span>
                    {p.phone && <span className="text-xs text-slate-400">{p.phone}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selected && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="reserved-claim-confirm">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-1">Confirm</div>
              <div className="text-sm text-slate-800">
                Booking <span className="font-semibold">{patientName}</span> into the reserved slot at
                <span className="font-semibold"> {locationLabel || slot.location_code}</span> on
                <span className="font-semibold"> {fmtDateISO(slot.date)} </span> at
                <span className="font-semibold"> {fmt12(slot.slot_time)}</span>.
              </div>
              <button
                type="button"
                data-testid="reserved-claim-change-patient"
                onClick={() => setSelected(null)}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Change patient
              </button>
            </div>
          )}

          {err && err.kind === "conflict" && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="reserved-claim-error-conflict">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Slot no longer available</div>
                <div className="text-xs mt-0.5">{err.msg}. The calendar has been refreshed.</div>
              </div>
            </div>
          )}
          {err && err.kind === "book_failed" && (
            <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="reserved-claim-error-book">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Booking failed at Raintree</div>
                <div className="text-xs mt-0.5">
                  The reserved slot has been <strong>reopened automatically</strong> — no phantom booking was created. Try again in a moment.
                  <span className="block mt-0.5 text-rose-700/70">Detail: {err.msg}</span>
                </div>
              </div>
            </div>
          )}
          {err && err.kind === "other" && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 inline-flex items-center gap-2" data-testid="reserved-claim-error-other">
              <AlertTriangle size={14} /> {err.msg}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50/50">
          <button
            type="button"
            data-testid="reserved-claim-cancel"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 rounded-lg font-medium"
          >
            {err ? "Close" : "Cancel"}
          </button>
          <button
            type="button"
            data-testid="reserved-claim-submit"
            onClick={claim}
            disabled={!selected || submitting || (err && err.kind === "conflict") || reopened}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Claim slot
          </button>
        </div>
      </div>
    </div>
  );
}
